import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, ArrowUpDown, FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '../ui';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatNumber = (value, decimals = 1) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

const getMarginLevel = (margin) => {
  if (margin >= 50) return 'high';
  if (margin >= 20) return 'medium';
  return 'low';
};

const getRevisionLabel = (rev) => {
  const revNum = parseInt(rev);
  if (!rev || revNum === 0) return null;
  if (revNum === 999) return 'Erosion';
  return `Rev ${revNum}`;
};

const ProjectProfitTable = ({ projects }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'profit', direction: 'desc' });
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Sort projects
  const sortedProjects = useMemo(() => {
    const sorted = [...projects];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null) aValue = sortConfig.direction === 'asc' ? Infinity : -Infinity;
      if (bValue == null) bValue = sortConfig.direction === 'asc' ? Infinity : -Infinity;

      // String comparison
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Number comparison
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [projects, sortConfig]);

  // Paginated projects
  const { paginatedProjects, totalPages } = useMemo(() => {
    const calculatedTotalPages = pageSize === 999999 ? 1 : Math.ceil(sortedProjects.length / pageSize);

    // Reset to page 1 if current page exceeds total
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }

    if (pageSize === 999999) {
      return { paginatedProjects: sortedProjects, totalPages: 1 };
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return {
      paginatedProjects: sortedProjects.slice(startIndex, endIndex),
      totalPages: calculatedTotalPages
    };
  }, [sortedProjects, currentPage, pageSize]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setExpandedRow(null); // Collapse expanded row on page change
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setExpandedRow(null);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={12} className="sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp size={12} className="sort-icon" />
      : <ChevronDown size={12} className="sort-icon" />;
  };

  if (projects.length === 0) {
    return (
      <div className="profit-table-section">
        <div className="empty-state">
          <FileText size={48} />
          <h3>No projects found</h3>
          <p>Try adjusting your filters to see more results.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profit-table-section">
      <div className="profit-table-header">
        <h3 className="profit-table-title">Project Details</h3>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          {pageSize === 999999 ? (
            `${projects.length} projects`
          ) : (
            `Page ${currentPage} of ${totalPages} (${projects.length} projects)`
          )}
        </span>
      </div>

      <div className="profit-table-container">
        <table className="profit-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th className={`sortable ${sortConfig.key === 'jobNumber' ? 'sorted' : ''}`}
                  onClick={() => handleSort('jobNumber')}>
                Job # {getSortIcon('jobNumber')}
              </th>
              <th className={`sortable ${sortConfig.key === 'projectName' ? 'sorted' : ''}`}
                  onClick={() => handleSort('projectName')}>
                Project {getSortIcon('projectName')}
              </th>
              <th className={`sortable ${sortConfig.key === 'clientName' ? 'sorted' : ''}`}
                  onClick={() => handleSort('clientName')}>
                Client {getSortIcon('clientName')}
              </th>
              <th className={`sortable ${sortConfig.key === 'initialAcceptance' ? 'sorted' : ''}`}
                  onClick={() => handleSort('initialAcceptance')}>
                Date {getSortIcon('initialAcceptance')}
              </th>
              <th className={`sortable ${sortConfig.key === 'acreage' ? 'sorted' : ''}`}
                  onClick={() => handleSort('acreage')}
                  style={{ textAlign: 'right' }}>
                Acres {getSortIcon('acreage')}
              </th>
              <th className={`sortable ${sortConfig.key === 'revenue' ? 'sorted' : ''}`}
                  onClick={() => handleSort('revenue')}
                  style={{ textAlign: 'right' }}>
                Revenue {getSortIcon('revenue')}
              </th>
              <th className={`sortable ${sortConfig.key === 'laborHours' ? 'sorted' : ''}`}
                  onClick={() => handleSort('laborHours')}
                  style={{ textAlign: 'right' }}>
                Hours {getSortIcon('laborHours')}
              </th>
              <th className={`sortable ${sortConfig.key === 'laborCost' ? 'sorted' : ''}`}
                  onClick={() => handleSort('laborCost')}
                  style={{ textAlign: 'right' }}>
                Labor Cost {getSortIcon('laborCost')}
              </th>
              <th className={`sortable ${sortConfig.key === 'profit' ? 'sorted' : ''}`}
                  onClick={() => handleSort('profit')}
                  style={{ textAlign: 'right' }}>
                Profit {getSortIcon('profit')}
              </th>
              <th className={`sortable ${sortConfig.key === 'marginPercent' ? 'sorted' : ''}`}
                  onClick={() => handleSort('marginPercent')}
                  style={{ textAlign: 'right' }}>
                Margin {getSortIcon('marginPercent')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedProjects.map((project) => {
              const isExpanded = expandedRow === project.projectId;
              const marginLevel = getMarginLevel(project.marginPercent);

              return (
                <React.Fragment key={project.projectId}>
                  <tr
                    className={`expandable ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => setExpandedRow(isExpanded ? null : project.projectId)}
                  >
                    <td>
                      <ChevronDown
                        size={16}
                        className="expand-icon"
                        style={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}
                      />
                    </td>
                    <td className="cell-job">
                      {project.jobNumber}
                      {getRevisionLabel(project.rev) && (
                        <span className="revision-badge" style={{
                          marginLeft: '0.5rem',
                          padding: '0.125rem 0.375rem',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          backgroundColor: parseInt(project.rev) === 999
                            ? 'var(--status-warning-bg)'
                            : 'var(--bg-tertiary)',
                          color: parseInt(project.rev) === 999
                            ? 'var(--status-warning)'
                            : 'var(--text-secondary)'
                        }}>
                          {getRevisionLabel(project.rev)}
                        </span>
                      )}
                    </td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {project.projectName}
                    </td>
                    <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {project.clientName || '—'}
                    </td>
                    <td>{project.initialAcceptance || '—'}</td>
                    <td className="cell-money" style={{ color: 'var(--text-secondary)' }}>
                      {project.acreage != null ? formatNumber(project.acreage, 2) : '—'}
                    </td>
                    <td className="cell-money">
                      {formatCurrency(project.revenue)}
                    </td>
                    <td className="cell-money" style={{ color: 'var(--text-secondary)' }}>
                      {formatNumber(project.laborHours)}
                    </td>
                    <td className="cell-money" style={{ color: 'var(--status-warning)' }}>
                      {formatCurrency(project.laborCost)}
                    </td>
                    <td className={`cell-money ${project.profit >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(project.profit)}
                    </td>
                    <td className="cell-percent">
                      <span className={`margin-badge ${marginLevel}`}>
                        {formatNumber(project.marginPercent)}%
                      </span>
                    </td>
                  </tr>

                  {/* Expanded Row Detail - Employee Breakdown */}
                  {isExpanded && (
                    <tr className="expanded-detail">
                      <td colSpan={11}>
                        <div className="employee-breakdown">
                          <div className="employee-breakdown-title">
                            Employee Breakdown
                          </div>
                          {(() => {
                            // Aggregate time entries by employee
                            const employeeMap = {};
                            (project.timeEntries || []).forEach(entry => {
                              const empId = entry.employee_id;
                              if (!empId) return;

                              if (!employeeMap[empId]) {
                                employeeMap[empId] = {
                                  name: entry.employeeName || 'Unknown',
                                  hours: 0,
                                  cost: 0,
                                  rate: entry.hourly_rate_used || 0
                                };
                              }
                              employeeMap[empId].hours += parseFloat(entry.duration_hours) || 0;
                              employeeMap[empId].cost += parseFloat(entry.labor_cost) || 0;
                              // Keep the most recent rate if it varies
                              if (entry.hourly_rate_used) {
                                employeeMap[empId].rate = entry.hourly_rate_used;
                              }
                            });

                            const employees = Object.values(employeeMap)
                              .sort((a, b) => b.hours - a.hours);

                            if (employees.length === 0) {
                              return (
                                <div style={{
                                  padding: '1rem',
                                  color: 'var(--text-secondary)',
                                  fontStyle: 'italic'
                                }}>
                                  No time entries recorded for this project
                                </div>
                              );
                            }

                            return (
                              <table className="employee-breakdown-table">
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left' }}>Employee</th>
                                    <th style={{ textAlign: 'right' }}>Hours</th>
                                    <th style={{ textAlign: 'right' }}>Rate</th>
                                    <th style={{ textAlign: 'right' }}>Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {employees.map((emp, idx) => (
                                    <tr key={idx}>
                                      <td>{emp.name}</td>
                                      <td style={{ textAlign: 'right' }}>{formatNumber(emp.hours)}</td>
                                      <td style={{ textAlign: 'right' }}>{formatCurrency(emp.rate)}/hr</td>
                                      <td style={{ textAlign: 'right', color: 'var(--status-warning)' }}>
                                        {formatCurrency(emp.cost)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="pagination-footer" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1rem',
        borderTop: '1px solid var(--border-primary)',
        backgroundColor: 'var(--surface-secondary)',
        borderRadius: '0 0 0.5rem 0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Button
            size="sm"
            variant={pageSize === 20 ? 'secondary' : 'ghost'}
            onClick={() => handlePageSizeChange(20)}
          >
            20
          </Button>
          <Button
            size="sm"
            variant={pageSize === 50 ? 'secondary' : 'ghost'}
            onClick={() => handlePageSizeChange(50)}
          >
            50
          </Button>
          <Button
            size="sm"
            variant={pageSize === 999999 ? 'secondary' : 'ghost'}
            onClick={() => handlePageSizeChange(999999)}
          >
            All
          </Button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft size={16} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
          </Button>

          {/* Page number buttons */}
          {(() => {
            const startPage = Math.max(1, currentPage - 2);
            const endPage = Math.min(totalPages, currentPage + 2);
            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <Button
                  key={i}
                  size="sm"
                  variant={i === currentPage ? 'secondary' : 'ghost'}
                  onClick={() => handlePageChange(i)}
                  style={{ minWidth: '2rem' }}
                >
                  {i}
                </Button>
              );
            }
            return pages;
          })()}

          <Button
            size="sm"
            variant="ghost"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={16} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectProfitTable;
